import { upperFirst } from 'lodash';
import { CalendarCheck2, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import useStore from '../../../../store';
import { getRentalPeriodYearsAndMonths, reFormatDate } from '../../utils';
import {
  StyledDescriptionList,
  StyledFullWidthDLItem,
  StyledLinkToProperty,
  StyledSection,
  StyledSectionTitle,
  StyledSectionTitleWrapper,
} from '../BookingInformation.styles';

interface BookingRentInformationProps {
  periodStart: string;
  periodEnd: string;
  propertyId: string;
  numberOfGuests: number;
}

const BookingRentInformation = ({
  periodStart,
  periodEnd,
  propertyId,
  numberOfGuests,
}: BookingRentInformationProps) => {
  const { locale } = useParams();
  const { t } = useTranslation(['booking', 'routes']);
  const checkInDate = periodStart;
  const checkOutDate = periodEnd;
  const rentalPeriod = getRentalPeriodYearsAndMonths(
    new Date(reFormatDate(periodStart)),
    new Date(reFormatDate(periodEnd)),
  );

  const { user } = useStore(state => ({
    user: state.user,
  }));
  const rentalPeriodYearsFormatted = `${rentalPeriod.years} ${t(
    'booking:years',
  )}`;
  const rentalPeriodFormatted =
    rentalPeriod.months > 0
      ? `${rentalPeriodYearsFormatted} and ${rentalPeriod.months} ${t(
          'booking:months',
        )}`
      : rentalPeriodYearsFormatted;

  return (
    <StyledSection>
      <StyledSectionTitleWrapper user={user?.type}>
        <CalendarCheck2 size={20} />
        <StyledSectionTitle>
          {upperFirst(t('booking:rentInformation'))}
        </StyledSectionTitle>
      </StyledSectionTitleWrapper>
      <StyledDescriptionList>
        <div>
          <dt>Check-in</dt>
          <dd>{checkInDate}</dd>
        </div>
        <div>
          <dt>Check-out</dt>
          <dd>{checkOutDate}</dd>
        </div>
        <div>
          <dt>{upperFirst(t('booking:rentalPeriod'))}</dt>
          <dd>{rentalPeriodFormatted}</dd>
        </div>
        <div>
          <dt>{upperFirst(t('booking:nGuests'))}</dt>
          <dd>{numberOfGuests}</dd>
        </div>
        <StyledFullWidthDLItem>
          <StyledLinkToProperty
            href={`${process.env.REACT_APP_SITE_URL}/${locale}/${t(
              'routes:rent-path-param',
            )}/${propertyId}`}
            target="_blank"
            rel="noreferrer"
          >
            {upperFirst(t('booking:seeProperty'))}
            <ExternalLink size={14} aria-hidden="true" />
          </StyledLinkToProperty>
        </StyledFullWidthDLItem>
      </StyledDescriptionList>
    </StyledSection>
  );
};

export default BookingRentInformation;
